/**
 * S21 — Vendors · Performance Comparison (`/vendors/performance`).
 * Cost / latency / coverage per vendor — like-for-like.
 */
import React, { useMemo } from "react";
import { Gauge, Activity, DollarSign } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import CsvExportButton from "@/components/data/CsvExportButton";
import { fmtPer1k } from "@/pages/agents/components/AgentPrimitives";
import VendorQuadrant from "./components/VendorQuadrant";
import { cn } from "@/lib/utils";

const A = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);

const VENDOR_CSV_COLUMNS = [
  { header: "Vendor ID",          accessor: (v) => v.id },
  { header: "Vendor",             accessor: (v) => v.name },
  { header: "Category",           accessor: (v) => v.category || "" },
  { header: "Agents governed",    accessor: (v) => v.agentsGoverned || 0 },
  { header: "Monthly spend (AUD)", accessor: (v) => v.monthlySpendAud || 0 },
  { header: "Latency P95 (ms)",   accessor: (v) => v.latencyMs || 0 },
  { header: "Status",             accessor: (v) => v.status || "" },
  { header: "Auth method",        accessor: (v) => v.authMethod || "" },
];

export default function VendorPerformance() {
  const { user } = useAuth();
  const { items: vendors, loading, error, lastSyncedAt, refetch } = useEntityList("/api/vendors");
  // Phase 4 — burn rollup comes from the live-computed metrics
  // endpoint instead of a precomputed bundle slice.
  const { items: vendorBurnRollup } = useEntityList("/api/metrics/vendor-burn");
  const { isCustomer } = useTenantData();

  const burnByVendor = useMemo(() => {
    const map = new Map();
    vendorBurnRollup.forEach((v) => map.set(v.id, v));
    return map;
  }, [vendorBurnRollup]);

  const totalSpend  = useMemo(() => vendors.reduce((a, v) => a + (v.monthlySpendAud || 0), 0), [vendors]);
  const maxLatency  = useMemo(() => Math.max(...vendors.map((v) => v.latencyMs || 0), 1),     [vendors]);
  const avgLatency  = useMemo(() => vendors.length ? Math.round(vendors.reduce((a, v) => a + (v.latencyMs || 0), 0) / vendors.length) : 0, [vendors]);
  const sortedVendors = useMemo(
    () => [...vendors].sort((a, b) => (b.monthlySpendAud || 0) - (a.monthlySpendAud || 0)),
    [vendors],
  );
  const bestVendor = useMemo(() => bestPriceLatency(vendors), [vendors]);

  if (loading && vendors.length === 0) return <VendorSkeleton />;
  if (error) return <VendorError message={error.message} />;
  if (isCustomer && !vendors.length) return <Empty userName={user?.name?.split(" ")[0]} />;

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="vendor-performance">
      <div className="absolute -right-24 -top-12 pointer-events-none"><Flywheel size={300} opacity={0.05} /></div>

      <div className="relative flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Vendors · Performance Comparison</p>
          <h1 className="text-display-md text-ink-primary mt-1">Vendor Performance</h1>
          <p className="text-ink-secondary mt-1 max-w-2xl">
            Cost vs latency vs coverage across every connected vendor — like-for-like
            on identical governed workloads.
          </p>
        </div>
        <SyncedIndicator
          lastSyncedAt={lastSyncedAt}
          loading={loading}
          onRefetch={refetch}
          testId="vendor-performance-synced"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <KpiTile label="Monthly AI spend"   value={A(totalSpend)}      delta={`${vendors.length} vendors`} deltaTone="neutral" hint="this billing cycle" icon={DollarSign} tone="tralenex" testId="vp-kpi-spend" />
        <KpiTile label="Avg P95 latency"    value={`${(avgLatency/1000).toFixed(2)}s`} delta="across all vendors"   deltaTone="neutral" hint="rolling 24h average (first-token + completion)"   icon={Activity}   tone="gold"     testId="vp-kpi-latency" />
        <KpiTile label="Best price/perf"    value={bestVendor} delta="lowest $/agent · low latency" deltaTone="up" hint="recommended for new agents" icon={Gauge}    tone="low"      testId="vp-kpi-best" />
      </div>

      <div className="mt-6">
        <VendorQuadrant vendors={vendors} burnByVendor={burnByVendor} />
      </div>

      <div className="mt-6 surface-card shadow-e1 overflow-hidden" data-testid="vp-table-panel">
        <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between gap-3">
          <p className="eyebrow">Vendor scorecard</p>
          <CsvExportButton
            rows={sortedVendors}
            columns={VENDOR_CSV_COLUMNS}
            filename={`vendors-${(sortedVendors[0]?.id || "tenant")}-${new Date().toISOString().slice(0,10)}.csv`}
            testId="vendor-csv-export"
          />
        </div>
        <table className="w-full text-sm" data-testid="vp-table">
          <thead className="bg-inset/40 text-ink-secondary text-meta uppercase tracking-[0.06em]">
            <tr>{["Vendor","Agents","Spend (mo)","Spend share","Wtd $/1k tok","P95 latency","Status"].map((h) => (
              <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {sortedVendors.map((v) => {
              const spendPct = ((v.monthlySpendAud || 0) / totalSpend) * 100;
              const latencyPct = ((v.latencyMs || 0) / maxLatency) * 100;
              const burn = burnByVendor.get(v.id);
              const wtdPer1k = burn?.weightedPer1kToken;
              return (
                <tr key={v.id} className="border-t border-border-subtle hover:bg-inset/30" data-testid={`vp-row-${v.id}`}>
                  <td className="px-3 py-2 text-ink-primary font-medium whitespace-nowrap">{v.name}</td>
                  <td className="px-3 py-2 text-ink-primary tabular-nums whitespace-nowrap">{v.agentsGoverned}</td>
                  <td className="px-3 py-2 text-ink-primary font-mono tabular-nums whitespace-nowrap">{A(v.monthlySpendAud || 0)}</td>
                  <td className="px-3 py-2 min-w-[160px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded bg-inset/60 overflow-hidden">
                        <div className="h-full bg-tralenex-500" style={{ width: `${spendPct}%` }} />
                      </div>
                      <span className="text-meta text-ink-secondary tabular-nums w-10 text-right">{spendPct.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td
                    className="px-3 py-2 text-ink-primary font-mono tabular-nums whitespace-nowrap"
                    data-testid={`vp-wtd-per1k-${v.id}`}
                  >
                    {fmtPer1k(wtdPer1k)}
                  </td>
                  <td className="px-3 py-2 min-w-[160px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded bg-inset/60 overflow-hidden">
                        <div className={cn("h-full", v.latencyMs > 2000 ? "bg-risk-high" : v.latencyMs > 1300 ? "bg-gold-600" : "bg-risk-low")} style={{ width: `${latencyPct}%` }} />
                      </div>
                      <span className="text-meta text-ink-primary tabular-nums w-16 text-right">{(v.latencyMs/1000).toFixed(2)}s</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={cn("inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.06em]",
                      v.status === "healthy" ? "bg-risk-low/10 text-risk-low" : "bg-gold-300/15 text-gold-700")}>{v.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function bestPriceLatency(vendors) {
  if (!vendors.length) return "—";
  const healthy = vendors.filter((v) => v.status === "healthy" && v.agentsGoverned > 0);
  // Score = (cost per governed agent) × (latency normalised to a 1.2s baseline).
  const ranked = healthy
    .map((v) => ({ v, score: (v.monthlySpendAud / Math.max(1, v.agentsGoverned)) * (v.latencyMs / 1200) }))
    .sort((a, b) => a.score - b.score);
  return ranked[0]?.v?.name || "—";
}

function Empty({ userName }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden" data-testid="vp-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none"><Flywheel size={620} opacity={0.04} /></div>
      <div className="relative max-w-2xl">
        <p className="eyebrow">Vendors · Performance Comparison</p>
        <h1 className="text-display-lg text-ink-primary mt-1">{userName ? `Welcome, ${userName}.` : "Welcome."}</h1>
        <p className="text-ink-secondary mt-3 text-[15px]">Connect vendors first — once traffic flows, this page surfaces cost-per-agent and latency comparisons.</p>
      </div>
    </div>
  );
}

// Phase 3b — load + error states for REST-fetched vendors.
function VendorSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="vendor-performance-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[360px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function VendorError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="vendor-performance-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Vendors · Performance Comparison</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load vendors</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}

